import * as React from 'react';

/** How long the peek stays once a menu opened from a row has closed. */
const PEEK_CLOSE_DELAY_MS = 400;

/** `closing` while it slides out, still in the open layout. */
export type AppSidebarPeek = 'open' | 'closing' | undefined;

/**
 * Whether the collapsed sidebar peeks: shows its open layout over the page, for the keyboard. The pointer has the rail's
 * tooltips and flyouts instead. The rules are those of Sentry's navigation (`useCollapsedNavigation`), without the
 * pointer's:
 * - it opens when the keyboard's focus lands in it;
 * - it stays while that focus is in it, and while a row says its popup is open (`aria-expanded`, not on a
 *   `data-disclosure` toggle);
 * - it closes when the focus moves on to the page, on Escape, on a click outside, and when the sidebar is pinned. It is
 *   `closing` first, while it slides out.
 *
 * `slideOutMs` is how long it stays `closing`; 0 skips that. `movingFocus` is set while the sidebar moves the focus
 * itself, which isn't the keyboard landing in it.
 */
export function useAppSidebarPeek(
    nav: React.RefObject<HTMLElement>,
    collapsed: boolean,
    slideOutMs: number,
    movingFocus: React.RefObject<boolean>,
): AppSidebarPeek {
    const [peek, setPeek] = React.useState<AppSidebarPeek>();
    // Pinned, it has ended: reset while rendering, so no frame shows the open sidebar laid out as a peek.
    if (!collapsed && peek !== undefined) {
        setPeek(undefined);
    }

    React.useEffect(() => {
        const element = nav.current;
        if (!collapsed || element === null) {
            return;
        }
        // The wait after a menu closed; the slide out.
        let timer: ReturnType<typeof setTimeout> | undefined;
        let slideOut: ReturnType<typeof setTimeout> | undefined;

        // Asked when it matters, not tracked: a popup that goes with the focus in it fires no event to track.
        const hasKeyboardFocus = () =>
            element.contains(document.activeElement) && document.activeElement?.matches(':focus-visible') === true;
        // A menu opened from a row is in a portal: the focus leaves the `nav` for it. An area's chevron opens none.
        const hasOpenMenu = () => element.querySelector('[aria-expanded="true"]:not([data-disclosure])') !== null;

        const open = () => {
            clearTimeout(timer);
            clearTimeout(slideOut);
            setPeek('open');
        };
        const close = () => {
            clearTimeout(timer);
            clearTimeout(slideOut);
            // Also from `closing`: a click outside closes it, and so does the focus that click moves.
            setPeek((current) => (current !== undefined && slideOutMs > 0 ? 'closing' : undefined));
            slideOut = setTimeout(() => setPeek(undefined), slideOutMs);
        };

        const closeOnceNothingHoldsIt = () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                if (!hasKeyboardFocus() && !hasOpenMenu()) {
                    close();
                }
            }, PEEK_CLOSE_DELAY_MS);
        };

        const onFocusIn = (event: FocusEvent) => {
            if (!movingFocus.current && event.target instanceof Element && event.target.matches(':focus-visible')) {
                open();
            }
        };
        const onFocusOut = (event: FocusEvent) => {
            // Between two of its rows the focus hasn't left, and closing here would show for a frame.
            const leaves = !(event.relatedTarget instanceof Node && element.contains(event.relatedTarget));
            if (leaves && !hasOpenMenu()) {
                close();
            }
        };
        // Not capturing: an open popup takes Escape first, and MUI's keep it from the document.
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };
        // Capturing, so no handler on the page can keep a click from the peek.
        const onMouseDown = (event: MouseEvent) => {
            // A click in a menu opened from a row is outside the `nav` too: the menu closes, and then the peek.
            if (!(event.target instanceof Node && element.contains(event.target)) && !hasOpenMenu()) {
                close();
            }
        };

        // A menu that closes holds the peek no longer; an area's chevron opens none.
        const menus = new MutationObserver((records) => {
            if (records.some(({ target }) => !(target instanceof Element && target.matches('[data-disclosure]')))) {
                closeOnceNothingHoldsIt();
            }
        });

        menus.observe(element, { subtree: true, attributeFilter: ['aria-expanded'] });
        element.addEventListener('focusin', onFocusIn);
        element.addEventListener('focusout', onFocusOut);
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('mousedown', onMouseDown, true);
        return () => {
            menus.disconnect();
            element.removeEventListener('focusin', onFocusIn);
            element.removeEventListener('focusout', onFocusOut);
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('mousedown', onMouseDown, true);
            clearTimeout(timer);
            clearTimeout(slideOut);
        };
    }, [nav, collapsed, slideOutMs, movingFocus]);

    return peek;
}
